// Каркас CLI-окна (README §2, §2б): колонка «лог + поле ввода».
//
//   <CliWindow>
//     <CliLog ref={logRef}>…строки…</CliLog>
//     <CliInput value={…} onChange={…} onSubmit={…} actions={…} below={…} />
//   </CliWindow>
//
// Лог и поле — соседи в одной flex-колонке: лог flex:1/min-height:0, поле
// снизу. Рост поля сжимает лог, а не рисуется поверх (§2б п.4). Лог сам
// следит за своим размером и содержимым: если пользователь был внизу (запас
// < 80 px), он докручивается вниз и при новом выводе, и при росте поля
// (п.5); если пролистал вверх — не прыгает и показывает «↓ новое» (п.6).
//
// Наполнение (строки лога, слэш-подсказки, вложения, голос) делает зона cli —
// здесь только поведение и слоты.

import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react'
import type {
  ClipboardEvent, CSSProperties, KeyboardEvent, ReactNode, Ref,
} from 'react'
import { Icon } from './Icon'
import './ui.css'

// ---------- окно ----------

export interface CliWindowProps {
  children: ReactNode
  /** идёт перенос файлов над окном — рамка 2px dashed --focus и подпись (§2в) */
  dropActive?: boolean
  dropLabel?: string
  className?: string
  style?: CSSProperties
  /** обработчики drag&drop вешает зона cli */
  onDragEnter?: React.DragEventHandler<HTMLDivElement>
  onDragOver?: React.DragEventHandler<HTMLDivElement>
  onDragLeave?: React.DragEventHandler<HTMLDivElement>
  onDrop?: React.DragEventHandler<HTMLDivElement>
}

export function CliWindow({
  children, dropActive, dropLabel = 'отпустите, чтобы прикрепить', className, style, ...drag
}: CliWindowProps) {
  return (
    <div className={`abx-cli${dropActive ? ' is-drop' : ''}${className ? ' ' + className : ''}`} style={style} {...drag}>
      {children}
      {dropActive ? <div className="abx-cli__drop">{dropLabel}</div> : null}
    </div>
  )
}

// ---------- лог ----------

export interface CliLogHandle {
  /** прокрутить в самый низ (и снять «↓ новое») */
  scrollToBottom: (smooth?: boolean) => void
  /** пользователь сейчас у низа (запас < threshold) */
  isAtBottom: () => boolean
  /** прокручиваемый элемент */
  element: HTMLDivElement | null
}

export interface CliLogProps {
  children: ReactNode
  /** запас до низа, при котором лог считается «внизу», px (§2б: 80) */
  threshold?: number
  /** подпись кнопки возврата вниз */
  newLabel?: string
  className?: string
  /** стиль внутренней колонки строк (отступы §2: 18px 32px 26px — по умолчанию) */
  contentStyle?: CSSProperties
  onAtBottomChange?: (atBottom: boolean) => void
}

export const CliLog = forwardRef(function CliLog(
  { children, threshold = 80, newLabel = '↓ новое', className, contentStyle, onAtBottomChange }: CliLogProps,
  ref: Ref<CliLogHandle>,
) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const atBottom = useRef(true)
  const lastContentH = useRef(0)
  const [hasNew, setHasNew] = useState(false)

  const measureBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold
  }, [threshold])

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
    atBottom.current = true
    setHasNew(false)
    onAtBottomChange?.(true)
  }, [onAtBottomChange])

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    isAtBottom: () => atBottom.current,
    element: scrollRef.current,
  }), [scrollToBottom])

  // Первый показ — сразу внизу, как терминал.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    const content = contentRef.current
    if (!el || !content) return
    lastContentH.current = content.offsetHeight
    // Одна функция на оба случая: сжалось окно лога (выросло поле ввода,
    // открылась клавиатура) или выросло содержимое (новый вывод).
    const ro = new ResizeObserver(() => {
      const grown = content.offsetHeight > lastContentH.current + 1
      lastContentH.current = content.offsetHeight
      if (atBottom.current) {
        el.scrollTop = el.scrollHeight
      } else if (grown) {
        setHasNew(true)
      }
    })
    ro.observe(el)
    ro.observe(content)
    return () => ro.disconnect()
  }, [])

  const onScroll = () => {
    const bottom = measureBottom()
    if (bottom !== atBottom.current) onAtBottomChange?.(bottom)
    atBottom.current = bottom
    if (bottom) setHasNew(false)
  }

  return (
    <div className={`abx-clilog${className ? ' ' + className : ''}`}>
      <div ref={scrollRef} className="abx-clilog__scroll" onScroll={onScroll}>
        <div ref={contentRef} className="abx-clilog__content" style={contentStyle}>
          {children}
        </div>
      </div>
      {hasNew ? (
        <button className="abx-clilog__new" onClick={() => scrollToBottom(true)}>{newLabel}</button>
      ) : null}
    </div>
  )
})

// ---------- поле ввода ----------

/** Высота строки поля: 24 px, padding 3/0 → однострочное поле 30 px = высоте кнопок. */
const LINE = 24
const AUTO_MAX = 180

export interface CliInputProps {
  value: string
  onChange: (value: string) => void
  /** ↵ без Shift. Не вызывается, пока submitBlocked (идёт загрузка вложений). */
  onSubmit: () => void
  placeholder?: string
  disabled?: boolean
  /** ↵ недоступен (например, файлы ещё грузятся, §2в п.3) */
  submitBlocked?: boolean
  /** слева от поля; по умолчанию «>» цветом --steel */
  prompt?: ReactNode
  /** справа, группа высотой 30: скрепка, «● голос» (CliAttachButton, CliVoiceButton) */
  actions?: ReactNode
  /** над полем внутри колонки: плашки вложений, «в контексте: …», подсказки команд */
  above?: ReactNode
  /** под полем: строка режима прав и модели 12 px --term-dim */
  below?: ReactNode
  /** перехват клавиш до ↵ (↑/↓ по подсказкам). true — событие обработано. */
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => boolean | void
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  /** доступ к textarea (фокус, вставка) */
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>
  autoFocus?: boolean
  className?: string
}

function viewportH(): number {
  return window.visualViewport?.height ?? window.innerHeight
}

export function CliInput({
  value, onChange, onSubmit, placeholder, disabled, submitBlocked, prompt, actions, above, below,
  onKeyDown, onPaste, textareaRef, autoFocus, className,
}: CliInputProps) {
  const ta = useRef<HTMLTextAreaElement | null>(null)
  const [autoH, setAutoH] = useState(LINE)
  const [needH, setNeedH] = useState(LINE)
  /** ручная высота; null — по тексту. Переживает отправку (§2б п.2). */
  const [userH, setUserH] = useState<number | null>(null)
  const drag = useRef<{ sy: number; h0: number; moved: boolean } | null>(null)

  const setRef = (el: HTMLTextAreaElement | null) => {
    ta.current = el
    if (textareaRef) textareaRef.current = el
  }

  // Рост от текста: меряем scrollHeight на нулевой высоте. Мерить надо и
  // при смене текста, и при смене ширины поля (узкое окно, появилась кнопка
  // «↑», шрифт догрузился) — иначе перенос длинной строки не растит поле.
  const measure = useCallback(() => {
    const el = ta.current
    if (!el || el.clientWidth === 0) return
    const prev = el.style.height
    const scroll = el.scrollTop
    el.style.height = '0px'
    const need = Math.max(LINE, el.scrollHeight - 6)
    el.style.height = prev
    el.scrollTop = scroll
    setNeedH(need)
    setAutoH(Math.min(AUTO_MAX, need))
  }, [])

  useLayoutEffect(measure, [value, measure])

  useEffect(() => {
    const el = ta.current
    if (!el) return
    let width = el.clientWidth
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return
      width = el.clientWidth
      measure()
    })
    ro.observe(el)
    void document.fonts?.ready.then(measure)
    return () => ro.disconnect()
  }, [measure])

  const height = userH ?? autoH
  // Скроллбар только когда текст не помещается (§2б п.3).
  const overflow = needH > height + 2 ? 'auto' : 'hidden'

  const onGripDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { sy: event.clientY, h0: ta.current?.offsetHeight ? ta.current.offsetHeight - 6 : height, moved: false }
    document.body.classList.add('abx-dragging-ns')
  }
  const onGripMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d) return
    // клик по ручке (дрожь 1–2 px) — не ручная высота: иначе поле
    // «замерзает» в одну строку и перестаёт расти от текста
    if (!d.moved && Math.abs(event.clientY - d.sy) < 4) return
    d.moved = true
    const max = Math.round(viewportH() * 0.7)
    setUserH(Math.min(max, Math.max(LINE, Math.round(d.h0 + (d.sy - event.clientY)))))
  }
  const onGripUp = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    document.body.classList.remove('abx-dragging-ns')
  }

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (onKeyDown?.(event) === true) return
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      if (!submitBlocked && !disabled) onSubmit()
    }
  }

  return (
    <div className={`abx-cliin${className ? ' ' + className : ''}`}>
      {above}
      <div className="abx-cliin__box">
        <div
          className="abx-cliin__grip"
          title="потяните вверх, чтобы увеличить поле · двойной клик — по размеру текста"
          onPointerDown={onGripDown}
          onPointerMove={onGripMove}
          onPointerUp={onGripUp}
          onPointerCancel={onGripUp}
          onDoubleClick={() => setUserH(null)}
        >
          <span />
        </div>
        <span className="abx-cliin__prompt">{prompt ?? '>'}</span>
        <textarea
          ref={setRef}
          className="abx-cliin__field"
          rows={1}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          spellCheck={false}
          style={{ height, overflowY: overflow }}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={keyDown}
          onPaste={onPaste}
        />
        {actions ? <div className="abx-cliin__actions">{actions}</div> : null}
      </div>
      {below ? <div className="abx-cliin__below">{below}</div> : null}
    </div>
  )
}

/** Скрепка 30×30, значок 16 px --term-dim (§2в п.1). */
export function CliAttachButton({ onClick, title = 'прикрепить файлы · можно перетащить или вставить из буфера' }: {
  onClick: () => void
  title?: string
}) {
  return (
    <button className="abx-cliin__attach" title={title} onClick={onClick}>
      <Icon name="paperclip" size={16} stroke={1.7} />
    </button>
  )
}

/** «● голос» — pill 30 px, hover --rec. active — идёт запись. */
export function CliVoiceButton({ onClick, active, label = 'голос' }: {
  onClick: () => void
  active?: boolean
  label?: string
}) {
  return (
    <button className={`abx-cliin__voice${active ? ' is-on' : ''}`} onClick={onClick}>
      <span className="abx-cliin__voicedot" />
      {label}
    </button>
  )
}
